import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store/store';
import { getClinicFeaturesApi } from '../rtk-apis/clinics/getClinicFeatures';
import { isDicomViewerEnabled, isMeasurementEnabled, type ClinicFeatures } from './clinicFeatures';

/**
 * Shared source of truth for the logged-in clinic's feature flags.
 *
 * Mirrors useClinicDropdowns: fetches once per session if nothing has been
 * loaded yet, and every consumer reads the same slice. Components should use
 * `dicomViewerEnabled` / `measurementEnabled` rather than the raw flags —
 * `measurementEnabled` also honours the build-time release gate.
 */
export const useClinicFeatures = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { features, loading, fetched } = useSelector(
    (state: RootState) => state.getClinicFeaturesResponse
  );

  useEffect(() => {
    if (!fetched && !loading) {
      dispatch(getClinicFeaturesApi());
    }
  }, [fetched, loading, dispatch]);

  return useMemo(
    () => ({
      features: features as ClinicFeatures,
      dicomViewerEnabled: isDicomViewerEnabled(features as ClinicFeatures),
      measurementEnabled: isMeasurementEnabled(features as ClinicFeatures),
      loading,
      refresh: () => dispatch(getClinicFeaturesApi()),
    }),
    [features, loading, dispatch]
  );
};
